import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import CodeBlock from "./CodeBlock";
import { quizzes, refs, exerciciosExtras } from "../data/guiaMotoresData";

// Tempos médios para concluir a etapa por completo: leitura, demos, parte dos
// recursos "Para ir além", quiz e os 20 exercícios extras de fixação.
const etapas = [
  { n: 0, label: "Início", meta: "o plano" },
  { n: 1, label: "Como um motor funciona", meta: "≈ 2h30" },
  { n: 2, label: "Motor DC, PWM e ponte H", meta: "≈ 3h" },
  { n: 3, label: "Servo e motor de passo", meta: "≈ 3h" },
  { n: 4, label: "Brushless, bateria e protótipo", meta: "≈ 2h30" },
];

const TOTAL = Object.values(quizzes).reduce((a, q) => a + q.length, 0);
const LETRAS = ["A", "B", "C", "D"];

// Estados da demonstração de ponte H (mesma lógica do guia original)
const hbModes = {
  fwd: {
    closed: ["S1", "S4"],
    path: "M40 20 L40 85 L93 85 M137 85 L190 85 L190 150",
    dir: "normal",
    run: true,
    txt: "Frente: S1 e S4 fechadas. A corrente entra pela esquerda do motor e sai pela direita.",
  },
  rev: {
    closed: ["S2", "S3"],
    path: "M190 20 L190 85 L137 85 M93 85 L40 85 L40 150",
    dir: "reverse",
    run: true,
    txt: "Ré: S2 e S3 fechadas. A corrente atravessa o motor no sentido oposto, e ele gira ao contrário.",
  },
  brake: {
    closed: ["S3", "S4"],
    path: "M40 150 L40 85 L93 85 M137 85 L190 85 L190 150",
    dir: "normal",
    run: false,
    txt: "Freio: S3 e S4 fechadas. Os dois fios do motor vão para o GND. O motor vira um gerador em curto e para rápido.",
  },
  coast: {
    closed: [],
    path: "",
    dir: "normal",
    run: false,
    txt: "Livre: nenhuma chave fechada. O motor fica desconectado e para devagar, por atrito.",
  },
};

const hbBotoes = [
  { mode: "fwd", label: "Frente" },
  { mode: "rev", label: "Ré" },
  { mode: "brake", label: "Freio" },
  { mode: "coast", label: "Livre" },
];

const hbChaves = { S1: [40, 52], S2: [190, 52], S3: [40, 118], S4: [190, 118] };

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function rotorStyle(duty) {
  if (duty < 8) return { animationPlayState: "paused" };
  return {
    animationPlayState: "running",
    animationDuration: (2.4 - (2.1 * duty) / 100).toFixed(2) + "s",
  };
}

function pwmPath(dutyPct) {
  const d = dutyPct / 100, periods = 4, w = 280, x0 = 10, hi = 15, lo = 80;
  let p = `M${x0} ${lo}`;
  for (let i = 0; i < periods; i++) {
    const s = x0 + (i * w) / periods, on = (w / periods) * d;
    if (d > 0) p += ` L${s} ${lo} L${s} ${hi} L${s + on} ${hi} L${s + on} ${lo}`;
  }
  return p + ` L${x0 + w} ${lo}`;
}

function servoPulsePath(us) {
  const x0 = 10, w = 200, hi = 10, lo = 45, on = w * (us / 20000);
  return `M${x0} ${lo} L${x0} ${hi} L${x0 + on} ${hi} L${x0 + on} ${lo} L${x0 + w} ${lo}`;
}

const Refs = ({ id }) => (
  <div className="guia-mot-refs">
    <div className="guia-mot-refs-title">Para ir além</div>
    <ul>
      {refs[id].map((r, i) => (
        <li key={i}>
          <span className="guia-mot-refs-kind">{r.k}</span>
          <a href={r.href} target="_blank" rel="noopener noreferrer">{r.t}</a> — {r.d}
        </li>
      ))}
    </ul>
  </div>
);

const MotorHeroSvg = ({ duty }) => (
  <svg width="140" height="140" viewBox="0 0 140 140" aria-hidden="true">
    <circle cx="70" cy="70" r="62" fill="var(--bs-tertiary-bg)" stroke="var(--bs-border-color)" strokeWidth="2" />
    <rect x="10" y="52" width="14" height="36" rx="3" fill="var(--bs-secondary-color)" />
    <rect x="116" y="52" width="14" height="36" rx="3" fill="var(--bs-secondary-color)" />
    <text x="17" y="74" fontSize="9" textAnchor="middle" fill="var(--bs-body-bg)">N</text>
    <text x="123" y="74" fontSize="9" textAnchor="middle" fill="var(--bs-body-bg)">S</text>
    <g className="guia-mot-rotor" style={{ transformOrigin: "70px 70px", ...rotorStyle(duty) }}>
      <rect x="30" y="62" width="80" height="16" rx="4" fill="var(--mot-accent)" />
      <rect x="62" y="30" width="16" height="80" rx="4" fill="var(--mot-accent)" />
      <circle cx="70" cy="70" r="10" fill="var(--bs-emphasis-color)" />
      <circle cx="70" cy="70" r="4" fill="var(--bs-body-bg)" />
    </g>
  </svg>
);

function GuiaMotores() {
  const [etapaAtiva, setEtapaAtiva] = useState(0);
  const [respostas, setRespostas] = useState({});
  const [respostasExtra, setRespostasExtra] = useState({});
  const [modalEtapa, setModalEtapa] = useState(null);
  const [heroDuty, setHeroDuty] = useState(0);
  const [pwmDuty, setPwmDuty] = useState(50);
  const [hbMode, setHbMode] = useState("coast");
  const [servoUs, setServoUs] = useState(1500);

  // Ordem aleatória criada uma vez por montagem: nos quizzes, perguntas E
  // alternativas embaralhadas; nos exercícios extras, só as alternativas.
  // As respostas são chaveadas pelos índices originais, imunes ao embaralhamento.
  const [ordem] = useState(() => {
    const o = {};
    for (const etapa of Object.keys(quizzes)) {
      o[etapa] = shuffle(
        quizzes[etapa].map((q, qi) => ({ qi, opts: shuffle(q.opts.map((_, oi) => oi)) }))
      );
    }
    return o;
  });
  const [ordemExtra] = useState(() => {
    const o = {};
    for (const etapa of Object.keys(exerciciosExtras)) {
      o[etapa] = exerciciosExtras[etapa].map((q) => shuffle(q.opts.map((_, oi) => oi)));
    }
    return o;
  });

  useEffect(() => {
    if (modalEtapa === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setModalEtapa(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalEtapa]);

  const irPara = (n) => {
    setEtapaAtiva(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const responder = (etapa, qi, oi, ok) => {
    const key = `${etapa}-${qi}`;
    setRespostas((prev) => (prev[key] !== undefined ? prev : { ...prev, [key]: { escolha: oi, ok } }));
  };

  const responderExtra = (etapa, qi, oi, ok) => {
    const key = `${etapa}-${qi}`;
    setRespostasExtra((prev) => (prev[key] !== undefined ? prev : { ...prev, [key]: { escolha: oi, ok } }));
  };

  const acertosTotal = Object.values(respostas).filter((r) => r.ok).length;

  const etapaConcluida = (etapa) =>
    quizzes[etapa].every((_, qi) => respostas[`${etapa}-${qi}`] !== undefined);

  // Renderiza uma questão genérica. optsOrder define a ordem de exibição das
  // alternativas; a resposta guarda o índice original, imune ao embaralhamento.
  const renderQuestao = (q, numero, resp, optsOrder, onResponder, chave) => {
    const corretaOriginal = q.opts.findIndex((o) => o.ok);
    return (
      <div key={chave} className="guia-mot-q">
        <div className="guia-mot-q-stem">{numero}. {q.stem}</div>
        <div className="guia-mot-q-opts">
          {optsOrder.map((oi, pos) => {
            const o = q.opts[oi];
            return (
              <button
                key={oi}
                disabled={resp !== undefined}
                className={
                  "guia-mot-q-opt" +
                  (resp !== undefined && o.ok ? " correct" : "") +
                  (resp !== undefined && resp.escolha === oi && !o.ok ? " wrong" : "")
                }
                onClick={() => onResponder(oi, o.ok)}
              >
                {LETRAS[pos]}) {o.t}
              </button>
            );
          })}
        </div>
        {resp !== undefined && (
          <div className={`guia-mot-q-why ${resp.ok ? "good" : "bad"}`}>
            <strong>{resp.ok ? "Certo." : "Errado."}</strong> {q.opts[resp.escolha].why}
            {!resp.ok && (
              <div className="guia-mot-q-extra">
                <strong>Resposta correta: {LETRAS[optsOrder.indexOf(corretaOriginal)]}.</strong>{" "}
                {q.opts[corretaOriginal].why}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderQuiz = (etapa) => {
    const qs = quizzes[etapa];
    const acertos = qs.filter((_, qi) => respostas[`${etapa}-${qi}`]?.ok).length;
    return (
      <div className="guia-mot-quiz">
        <h3 className="guia-mot-h3">Quiz da Etapa {etapa}</h3>
        {ordem[etapa].map((item, pos) =>
          renderQuestao(
            qs[item.qi],
            pos + 1,
            respostas[`${etapa}-${item.qi}`],
            item.opts,
            (oi, ok) => responder(etapa, item.qi, oi, ok),
            item.qi
          )
        )}
        {etapaConcluida(etapa) && (
          <div className="guia-mot-q-result">
            <strong>Etapa {etapa} concluída:</strong> {acertos} de {qs.length} acertos.{" "}
            {acertos === qs.length
              ? "Perfeito, pode seguir em frente."
              : acertos >= qs.length - 1
              ? "Boa. Releia a justificativa da questão errada antes de seguir."
              : "Vale reler as seções correspondentes antes de avançar; a próxima etapa depende delas."}
          </div>
        )}
        <button className="guia-mot-extra-btn" onClick={() => setModalEtapa(etapa)}>
          <span className="guia-mot-extra-btn-title">EXERCÍCIOS EXTRAS DE FIXAÇÃO</span>
          <span className="guia-mot-extra-btn-sub">20 questões para consolidar a Etapa {etapa}</span>
        </button>
      </div>
    );
  };

  const hb = hbModes[hbMode];
  const servoAng = ((servoUs - 1000) / 1000) * 180;

  return (
    <Container className="guia-mot mb-5 transicao" data-bs-theme="dark">
      <h1>MOTORES PARA ROBÔS</h1>
      <hr />
      <p className="text-muted" style={{ fontFamily: "var(--mot-mono)", fontSize: "13px" }}>
        Roadmap em 4 etapas · Guia interativo
      </p>

      <div className="guia-mot-section-nav">
        {etapas.map((d) => {
          const done = d.n >= 1 && etapaConcluida(d.n);
          return (
            <button
              key={d.n}
              className={
                "guia-mot-section-btn" +
                (etapaAtiva === d.n ? " active" : "") +
                (done ? " done" : "")
              }
              onClick={() => irPara(d.n)}
              title={d.meta}
            >
              <span className="guia-mot-section-num">{d.n === 0 ? "•" : done ? "✓" : d.n}</span>
              {d.label}
            </button>
          );
        })}
      </div>

      <div className="guia-mot-score">
        <span>
          Acertos no total: <strong>{acertosTotal} / {TOTAL}</strong>
        </span>
        <div className="guia-mot-score-bar">
          <div style={{ width: `${(100 * acertosTotal) / TOTAL}%` }} />
        </div>
      </div>

      {/* ===================== INÍCIO ===================== */}
      {etapaAtiva === 0 && (
        <div>
          <h2 className="guia-mot-h2">Motores aplicados a eletrônica e robótica</h2>
          <p className="guia-mot-lead">
            Um roadmap em 4 etapas para quem nunca ligou um motor na vida e quer chegar ao fim sabendo
            escolher, alimentar e controlar os motores de um protótipo de robô.
          </p>

          <div className="guia-mot-demo">
            <div className="guia-mot-demo-title">Antes de começar: gire este motor</div>
            <div className="guia-mot-demo-row">
              <MotorHeroSvg duty={heroDuty} />
              <div className="guia-mot-demo-ctrl">
                <label htmlFor="motHeroDuty">Quantidade de energia entregue (duty cycle do PWM)</label>
                <input
                  type="range"
                  id="motHeroDuty"
                  min="0"
                  max="100"
                  value={heroDuty}
                  onChange={(e) => setHeroDuty(+e.target.value)}
                />
                <div className="guia-mot-demo-read">
                  <span>{heroDuty} %</span>
                  <small>
                    Ao longo destas 4 etapas você vai entender exatamente o que este controle faz, por que
                    o pino do Arduino não pode fazê-lo sozinho e como escolher o motor certo para o seu robô.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="guia-mot-plan">
            <div><strong>Etapa 1 · ≈ 2h30</strong>Física básica, as 5 grandezas, por que precisa de driver, os 4 tipos de motor</div>
            <div><strong>Etapa 2 · ≈ 3h</strong>Motor DC escovado, redução, PWM e ponte H (o motor das rodas)</div>
            <div><strong>Etapa 3 · ≈ 3h</strong>Servo e motor de passo: controle de ângulo e posição</div>
            <div><strong>Etapa 4 · ≈ 2h30</strong>Brushless, baterias, cálculo de torque e o primeiro protótipo</div>
          </div>
          <p className="guia-mot-plan-nota">
            Tempos médios estimados para concluir cada etapa <strong>por completo</strong>: leitura e
            demonstrações, os principais recursos de "Para ir além" (vídeos e leituras indicados), o quiz e as
            20 questões dos exercícios extras de fixação. Total previsto: <strong>≈ 11 h</strong>. O ritmo
            varia de pessoa para pessoa — use os valores como referência, não como meta.
          </p>

          <h3 className="guia-mot-h3">Como usar</h3>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Orientação</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>Cada etapa tem leitura, demonstrações interativas, um quiz de 4 perguntas e 20 exercícios extras de fixação. Toda alternativa tem uma justificativa, mesmo as erradas.</td></tr>
                <tr><td>2</td><td>Faça uma etapa por vez. O conteúdo de cada etapa depende da anterior.</td></tr>
                <tr><td>3</td><td>Cada assunto termina com um bloco "Para ir além": vídeos e tutoriais externos, marcados como <strong>vídeo</strong>, <strong>leitura</strong> ou <strong>ferramenta</strong>, com aviso quando estão em inglês.</td></tr>
                <tr><td>4</td><td>Não precisa de material físico para acompanhar: a Etapa 4 termina com um robô-exemplo mostrando onde cada componente estudado se encaixa.</td></tr>
              </tbody>
            </table>
          </div>
          <div className="guia-mot-nav-btns">
            <button className="guia-mot-btn primary" onClick={() => irPara(1)}>Começar a Etapa 1 →</button>
          </div>
        </div>
      )}

      {/* ===================== ETAPA 1 ===================== */}
      {etapaAtiva === 1 && (
        <div>
          <div className="guia-mot-day-head">
            <span className="guia-mot-day-num">1</span>
            <h2>Como um motor funciona e o que os números significam</h2>
            <span className="guia-mot-day-time">≈ 2h30</span>
          </div>
          <div className="guia-mot-goal">
            <strong>Ao final da etapa você saberá:</strong> por que eletricidade vira giro, ler
            tensão/corrente/torque/RPM em uma ficha técnica, e por que ligar um motor direto no Arduino é um erro.
          </div>

          <h3 className="guia-mot-h3">1.1 Do ímã ao movimento</h3>
          <p>
            Toda corrente elétrica que passa por um fio cria um campo magnético ao redor dele. Se você enrola o
            fio em uma bobina, o campo fica mais forte e a bobina passa a se comportar como um ímã que você liga
            e desliga. Coloque essa bobina perto de um ímã permanente e ela será atraída ou repelida.
          </p>
          <p>
            Um motor é apenas isso, organizado com esperteza: uma parte fixa (o <strong>estator</strong>) e uma
            parte que gira (o <strong>rotor</strong>). Um deles tem ímãs, o outro tem bobinas. Ligando e
            desligando as bobinas na sequência certa, o rotor é "puxado" continuamente e gira.{" "}
            <strong>Motor = eletricidade → campo magnético → força → rotação.</strong>
          </p>
          <p>
            Toda a diferença entre os tipos de motor está em <em>quem</em> faz essa sequência de liga/desliga:
            uma peça mecânica (motor DC escovado), um chip externo (brushless e motor de passo) ou um circuito
            interno com sensor (servo).
          </p>
          <Refs id="1.1" />

          <h3 className="guia-mot-h3">1.2 As cinco grandezas que aparecem em toda ficha técnica</h3>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>Grandeza</th><th>Unidade</th><th>Analogia com água</th><th>O que importa para você</th></tr>
              </thead>
              <tbody>
                <tr><td>Tensão</td><td>V (volt)</td><td>Pressão na mangueira</td><td>Tem que bater com a bateria. Motor de 6 V em 12 V esquenta e queima; em 3 V gira fraco.</td></tr>
                <tr><td>Corrente</td><td>A (ampère)</td><td>Vazão de água</td><td>O motor "puxa" o que precisa. Define o driver e a bateria.</td></tr>
                <tr><td>Torque</td><td>N·m ou kgf·cm</td><td>Força da roda d'água</td><td>Força de giro. Decide se o robô sobe a rampa ou o braço levanta o objeto.</td></tr>
                <tr><td>Velocidade</td><td>RPM (rotações/min)</td><td>Quão rápido a roda gira</td><td>Sozinha não diz nada: motor rápido costuma ser fraco.</td></tr>
                <tr><td>Potência</td><td>W (watt)</td><td>Pressão × vazão</td><td>P = V × I. Diz quanto o motor consome. Boa parte vira calor.</td></tr>
              </tbody>
            </table>
          </div>
          <div className="guia-mot-note">
            <strong>Conversão útil:</strong> 1 kgf·cm ≈ 0,098 N·m (pense em "quase 0,1"). Fichas chinesas usam
            kgf·cm; fichas técnicas sérias usam N·m ou mN·m.
          </div>

          <p>Dois valores de corrente aparecem sempre e confundem iniciantes:</p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Tipo de corrente</th><th>O que significa</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td><strong>Corrente sem carga</strong> (no-load)</td><td>O motor girando livre, sem nada preso ao eixo. É o menor valor. Ex.: 120 mA.</td></tr>
                <tr><td>2</td><td><strong>Corrente de travamento</strong> (stall)</td><td>O motor tentando girar mas preso. É o <em>maior</em> valor, e é o que acontece no instante em que o robô arranca ou bate na parede. Ex.: 1,5 A. <strong>Dimensione o driver e a bateria pela corrente de stall</strong>, não pela corrente sem carga.</td></tr>
              </tbody>
            </table>
          </div>
          <Refs id="1.2" />

          <h3 className="guia-mot-h3">1.3 Por que o motor não pode ser ligado no pino do Arduino</h3>
          <p>
            Um pino de saída de um microcontrolador (Arduino, ESP32, Raspberry Pi Pico) entrega no máximo uns 20
            a 40 mA. O menor motor de brinquedo pede 100 a 200 mA só para girar, e pode passar de 1 A na partida.
            Três coisas acontecem se você ligar direto:
          </p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>O que acontece</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>O pino não consegue fornecer corrente suficiente: o motor gira fraco ou nem gira.</td></tr>
                <tr><td>2</td><td>O microcontrolador pode queimar o pino ou resetar.</td></tr>
                <tr><td>3</td><td>Ao desligar, a bobina do motor devolve um pico de tensão (o "chute" indutivo) que danifica o chip.</td></tr>
              </tbody>
            </table>
          </div>
          <p>A solução tem sempre a mesma forma, em qualquer tipo de motor:</p>
          <div className="guia-mot-note">
            <strong>Microcontrolador (sinal fraco) → Driver (chaveia a potência) → Motor</strong>, com uma{" "}
            <strong>fonte separada para o motor</strong> e o <strong>GND (negativo) compartilhado</strong> entre
            o microcontrolador e o driver. Sem o GND comum, o sinal de controle não tem referência e nada funciona.
          </div>
          <p>
            O "driver" muda de nome conforme o motor: ponte H para motor DC, driver de passo para motor de passo,
            ESC para brushless. O servo já vem com o driver dentro da carcaça.
          </p>
          <Refs id="1.3" />

          <h3 className="guia-mot-h3">1.4 Os quatro tipos que você vai encontrar em robôs</h3>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>Tipo</th><th>Controla o quê</th><th>Uso típico</th><th>Estuda em</th></tr>
              </thead>
              <tbody>
                <tr><td>DC escovado (com caixa de redução)</td><td>Velocidade e sentido</td><td>Rodas, esteiras, ventoinhas</td><td>Etapa 2</td></tr>
                <tr><td>Servo</td><td>Ângulo (0–180°)</td><td>Garras, braços, direção, cabeça de sensor</td><td>Etapa 3</td></tr>
                <tr><td>Motor de passo</td><td>Posição exata, em passos</td><td>Impressora 3D, CNC, plotter, torre</td><td>Etapa 3</td></tr>
                <tr><td>Brushless (BLDC)</td><td>Velocidade alta com eficiência</td><td>Drones, robô de combate, rodas rápidas</td><td>Etapa 4</td></tr>
              </tbody>
            </table>
          </div>

          {renderQuiz(1)}
          <div className="guia-mot-nav-btns">
            <button className="guia-mot-btn" onClick={() => irPara(0)}>← Início</button>
            <button className="guia-mot-btn primary" onClick={() => irPara(2)}>Ir para a Etapa 2 →</button>
          </div>
        </div>
      )}

      {/* ===================== ETAPA 2 ===================== */}
      {etapaAtiva === 2 && (
        <div>
          <div className="guia-mot-day-head">
            <span className="guia-mot-day-num">2</span>
            <h2>Motor DC escovado: PWM e ponte H</h2>
            <span className="guia-mot-day-time">≈ 3h</span>
          </div>
          <div className="guia-mot-goal">
            <strong>Ao final da etapa você saberá:</strong> como o motor das rodas funciona por dentro, o que a
            caixa de redução faz, como controlar velocidade com PWM e sentido com ponte H, e como os drivers mais comuns se comparam.
          </div>

          <h3 className="guia-mot-h3">2.1 Anatomia do motor DC escovado</h3>
          <p>É o motor mais simples e mais barato: dois fios, liga em uma pilha e gira. Por dentro:</p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Peça</th><th>Função</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td><strong>Estator</strong></td><td>Dois ímãs permanentes fixos na carcaça.</td></tr>
                <tr><td>2</td><td><strong>Rotor</strong></td><td>Bobinas de cobre enroladas em um núcleo de ferro, presas ao eixo.</td></tr>
                <tr><td>3</td><td><strong>Comutador</strong></td><td>Um anel de cobre segmentado, preso ao eixo, que gira junto.</td></tr>
                <tr><td>4</td><td><strong>Escovas</strong></td><td>Dois contatos de carvão que encostam no comutador e levam corrente às bobinas.</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            O comutador é o truque: a cada meia volta ele inverte sozinho o sentido da corrente na bobina, para
            que o rotor continue sendo puxado na mesma direção. É uma "sequência de liga/desliga" feita
            mecanicamente. O preço disso: as escovas se desgastam, fazem faísca e geram ruído elétrico. Para
            protótipos, tudo bem.
          </p>
          <p><strong>Inverter os dois fios inverte o sentido de giro.</strong> Guarde isso: é a base da ponte H.</p>
          <Refs id="2.1" />

          <h3 className="guia-mot-h3">2.2 Caixa de redução: trocar velocidade por torque</h3>
          <p>
            Um motor DC pequeno gira rápido demais e é fraco demais para mover um robô (tipicamente 10 000 RPM e
            um torque desprezível). A caixa de redução é um conjunto de engrenagens que divide a velocidade e
            multiplica o torque pelo mesmo fator (menos perdas).
          </p>
          <p>
            O motor amarelo mais comum em kits, o <strong>"motor TT"</strong>, tem redução de <strong>1:48</strong>:
            o motor interno gira 48 vezes para a roda dar uma volta. Resultado em 6 V: cerca de 200 RPM e 0,8
            kgf·cm de torque. Perfeito para um robô de 500 g a 1 kg.
          </p>
          <div className="guia-mot-note">
            <strong>Regra prática:</strong> velocidade × torque ≈ constante para um mesmo motor. Quer mais força?
            Aceite girar mais devagar (maior redução). Quer velocidade? Aceite menos força.
          </div>

          <h3 className="guia-mot-h3">2.3 Controlar a velocidade: PWM</h3>
          <p>
            Um microcontrolador não sabe gerar "3,7 V". Ele só sabe ligar (5 V) e desligar (0 V). O truque é
            ligar e desligar muito rápido, centenas ou milhares de vezes por segundo, e variar a fração do tempo
            em que fica ligado. Essa fração é o <strong>duty cycle</strong>. O motor, por ter inércia e
            indutância, não acompanha os pulsos e "sente" apenas a média:
          </p>
          <p className="guia-mot-formula">V<sub>média</sub> = V<sub>fonte</sub> × duty cycle</p>

          <div className="guia-mot-demo">
            <div className="guia-mot-demo-title">Demonstração: PWM em um motor de 6 V</div>
            <div className="guia-mot-demo-row">
              <svg width="300" height="90" viewBox="0 0 300 90" aria-hidden="true">
                <line x1="10" y1="80" x2="290" y2="80" stroke="var(--bs-border-color)" />
                <line x1="10" y1="15" x2="290" y2="15" stroke="var(--bs-border-color)" strokeDasharray="3 3" />
                <text x="12" y="12" fontSize="9" fill="var(--bs-secondary-color)">6 V</text>
                <text x="12" y="90" fontSize="9" fill="var(--bs-secondary-color)">0 V</text>
                <path d={pwmPath(pwmDuty)} fill="none" stroke="var(--mot-blue)" strokeWidth="2" />
                <line
                  x1="10" x2="290"
                  y1={80 - (65 * pwmDuty) / 100}
                  y2={80 - (65 * pwmDuty) / 100}
                  stroke="var(--mot-accent)" strokeWidth="2" strokeDasharray="6 3"
                />
                <text
                  x="292" y={77 - (65 * pwmDuty) / 100}
                  fontSize="9" fill="var(--mot-accent)" textAnchor="end"
                >
                  média
                </text>
              </svg>
              <svg width="90" height="90" viewBox="0 0 140 140" aria-hidden="true">
                <circle cx="70" cy="70" r="62" fill="var(--bs-tertiary-bg)" stroke="var(--bs-border-color)" strokeWidth="2" />
                <g className="guia-mot-rotor" style={{ transformOrigin: "70px 70px", ...rotorStyle(pwmDuty) }}>
                  <rect x="30" y="62" width="80" height="16" rx="4" fill="var(--mot-accent)" />
                  <rect x="62" y="30" width="16" height="80" rx="4" fill="var(--mot-accent)" />
                  <circle cx="70" cy="70" r="10" fill="var(--bs-emphasis-color)" />
                </g>
              </svg>
              <div className="guia-mot-demo-ctrl">
                <label htmlFor="motPwmDuty">Duty cycle</label>
                <input
                  type="range"
                  id="motPwmDuty"
                  min="0"
                  max="100"
                  value={pwmDuty}
                  onChange={(e) => setPwmDuty(+e.target.value)}
                />
                <div className="guia-mot-demo-read">
                  <span>{pwmDuty} % → {((6 * pwmDuty) / 100).toFixed(1).replace(".", ",")} V médios</span>
                  <small>
                    No Arduino: <code>analogWrite(pino, 0..255)</code>. 128 ≈ 50 %. Abaixo de uns 20–30 % o
                    motor com redução costuma não vencer o atrito e fica parado, zumbindo.
                  </small>
                </div>
              </div>
            </div>
          </div>
          <Refs id="2.3" />

          <h3 className="guia-mot-h3">2.4 Controlar o sentido: ponte H</h3>
          <p>
            Para inverter o giro é preciso inverter a polaridade nos dois fios do motor. A ponte H faz isso com
            quatro chaves eletrônicas (transistores) dispostas como a letra H, com o motor na barra do meio.
            Fechando as chaves em diagonal, a corrente atravessa o motor em um sentido; fechando a outra
            diagonal, no sentido oposto.
          </p>

          <div className="guia-mot-demo">
            <div className="guia-mot-demo-title">Demonstração: as quatro chaves da ponte H</div>
            <div className="guia-mot-demo-row">
              <svg width="230" height="170" viewBox="0 0 230 170" aria-hidden="true">
                <text x="115" y="14" fontSize="10" textAnchor="middle" fill="var(--bs-secondary-color)">+ bateria</text>
                <text x="115" y="166" fontSize="10" textAnchor="middle" fill="var(--bs-secondary-color)">GND</text>
                <line x1="40" y1="20" x2="190" y2="20" stroke="var(--bs-secondary-color)" strokeWidth="2" />
                <line x1="40" y1="150" x2="190" y2="150" stroke="var(--bs-secondary-color)" strokeWidth="2" />
                <line x1="40" y1="20" x2="40" y2="150" stroke="var(--bs-secondary-color)" strokeWidth="2" />
                <line x1="190" y1="20" x2="190" y2="150" stroke="var(--bs-secondary-color)" strokeWidth="2" />
                <line x1="40" y1="85" x2="190" y2="85" stroke="var(--bs-secondary-color)" strokeWidth="2" />
                {Object.entries(hbChaves).map(([nome, [x, y]]) => {
                  const on = hb.closed.includes(nome);
                  return (
                    <g key={nome}>
                      <rect
                        x={x - 9} y={y - 12} width="18" height="24" rx="3"
                        fill={on ? "var(--mot-blue)" : "var(--bs-secondary-bg)"}
                        stroke={on ? "var(--mot-blue)" : "var(--bs-secondary-color)"}
                        strokeWidth="2"
                      />
                      <text
                        x={x} y={y + 4} fontSize="9" textAnchor="middle"
                        fill={on ? "#fff" : "var(--bs-secondary-color)"}
                      >
                        {nome}
                      </text>
                    </g>
                  );
                })}
                {hb.path && (
                  <path d={hb.path} fill="none" stroke="var(--mot-accent)" strokeWidth="4" strokeLinecap="round" opacity="0.85" />
                )}
                <g
                  className="guia-mot-rotor"
                  style={{
                    transformOrigin: "115px 85px",
                    animationDirection: hb.dir,
                    animationDuration: "1.2s",
                    animationPlayState: hb.run ? "running" : "paused",
                  }}
                >
                  <circle cx="115" cy="85" r="22" fill="var(--bs-tertiary-bg)" stroke="var(--bs-border-color)" strokeWidth="2" />
                  <rect x="97" y="81" width="36" height="8" rx="2" fill="var(--mot-accent)" />
                  <rect x="111" y="67" width="8" height="36" rx="2" fill="var(--mot-accent)" />
                </g>
              </svg>
              <div className="guia-mot-demo-ctrl">
                <div>
                  {hbBotoes.map((b) => (
                    <button
                      key={b.mode}
                      className={`guia-mot-mode-btn${hbMode === b.mode ? " on" : ""}`}
                      onClick={() => setHbMode(b.mode)}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
                <div className="guia-mot-demo-read">
                  <small>{hb.txt}</small>
                </div>
              </div>
            </div>
          </div>

          <div className="guia-mot-warn">
            <strong>Nunca</strong> feche as duas chaves do mesmo lado (S1 e S3, ou S2 e S4). Isso liga o + da
            bateria direto no GND: curto-circuito, chamado <em>shoot-through</em>. Os chips de driver têm
            proteção contra isso; uma ponte H montada com transistores avulsos, não.
          </div>
          <Refs id="2.4" />

          <h3 className="guia-mot-h3">2.5 Comparando os drivers mais comuns</h3>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>Driver</th><th>Tecnologia</th><th>Corrente por motor</th><th>Observação</th></tr>
              </thead>
              <tbody>
                <tr><td>L298N</td><td>Bipolar (antigo)</td><td>2 A</td><td>Aparece em todo kit. Perde cerca de 2 V internamente: motor de 6 V recebe 4 V. Esquenta. Funciona, mas é ruim.</td></tr>
                <tr><td>TB6612FNG</td><td>MOSFET</td><td>1,2 A (3,2 A pico)</td><td>Perda desprezível, pequeno, dois motores. A escolha certa para motores TT.</td></tr>
                <tr><td>DRV8833</td><td>MOSFET</td><td>1,5 A</td><td>Parecido com o TB6612, tensão máxima menor (10,8 V).</td></tr>
                <tr><td>BTS7960</td><td>MOSFET</td><td>até 43 A</td><td>Para motores grandes (cadeira de rodas, robô de combate).</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Todo driver moderno de motor DC já inclui os <strong>diodos de roda livre</strong> (flyback), que
            absorvem o "chute" indutivo mencionado na Etapa 1. Se um dia você montar uma ponte H manualmente, eles
            são obrigatórios.
          </p>
          <Refs id="2.5" />

          <h3 className="guia-mot-h3">2.6 A ligação típica</h3>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Ligação</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>Bateria (6 a 12 V) → entrada de potência do driver (VM, VCC ou +12V).</td></tr>
                <tr><td>2</td><td>5 V do Arduino → entrada lógica do driver (VCC lógico), se o driver pedir.</td></tr>
                <tr><td>3</td><td><strong>GND da bateria, GND do driver e GND do Arduino: todos juntos.</strong></td></tr>
                <tr><td>4</td><td>Dois pinos digitais do Arduino → IN1 e IN2 (sentido). Um pino PWM (com "~") → PWM (velocidade).</td></tr>
                <tr><td>5</td><td>Saídas do driver → os dois fios do motor.</td></tr>
              </tbody>
            </table>
          </div>
          <CodeBlock code={`// Um motor no TB6612FNG
const int AIN1 = 7, AIN2 = 8, PWMA = 9, STBY = 6;

void setup() {
  pinMode(AIN1, OUTPUT); pinMode(AIN2, OUTPUT);
  pinMode(PWMA, OUTPUT); pinMode(STBY, OUTPUT);
  digitalWrite(STBY, HIGH);          // tira o driver do modo de espera
}

void motor(int velocidade) {          // -255 (ré) .. 255 (frente)
  digitalWrite(AIN1, velocidade > 0);
  digitalWrite(AIN2, velocidade < 0);
  analogWrite(PWMA, abs(velocidade)); // PWM = velocidade
}

void loop() {
  motor(180);  delay(2000);           // frente, ~70 %
  motor(-180); delay(2000);           // ré
  motor(0);    delay(1000);           // parado
}`} />
          <Refs id="2.6" />

          {renderQuiz(2)}
          <div className="guia-mot-nav-btns">
            <button className="guia-mot-btn" onClick={() => irPara(1)}>← Etapa 1</button>
            <button className="guia-mot-btn primary" onClick={() => irPara(3)}>Ir para a Etapa 3 →</button>
          </div>
        </div>
      )}

      {/* ===================== ETAPA 3 ===================== */}
      {etapaAtiva === 3 && (
        <div>
          <div className="guia-mot-day-head">
            <span className="guia-mot-day-num">3</span>
            <h2>Servo e motor de passo: controle de posição</h2>
            <span className="guia-mot-day-time">≈ 3h</span>
          </div>
          <div className="guia-mot-goal">
            <strong>Ao final da etapa você saberá:</strong> como um servo entende "vá para 90°", como alimentá-lo
            sem resetar o Arduino, como um motor de passo se move sem sensor, e quando usar cada um.
          </div>

          <h3 className="guia-mot-h3">3.1 Servo: um motor DC com cérebro próprio</h3>
          <p>
            Abra um servo de hobby e você encontra: um motor DC pequeno, uma caixa de redução, um{" "}
            <strong>potenciômetro</strong> preso ao eixo de saída e uma placa de controle. O potenciômetro mede o
            ângulo atual; a placa compara com o ângulo pedido e gira o motor até os dois coincidirem. É um
            sistema de <strong>malha fechada</strong> em miniatura.
          </p>
          <p>
            Você pede o ângulo com um pulso elétrico repetido 50 vezes por segundo (a cada 20 ms). O que importa
            é a <strong>largura do pulso</strong>:
          </p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Largura do pulso</th><th>Ângulo</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>1,0 ms</td><td>0°</td></tr>
                <tr><td>2</td><td>1,5 ms</td><td>90° (centro)</td></tr>
                <tr><td>3</td><td>2,0 ms</td><td>180°</td></tr>
              </tbody>
            </table>
          </div>

          <div className="guia-mot-demo">
            <div className="guia-mot-demo-title">Demonstração: largura do pulso → ângulo</div>
            <div className="guia-mot-demo-row">
              <svg width="200" height="130" viewBox="0 0 200 130" aria-hidden="true">
                <path d="M20 110 A80 80 0 0 1 180 110" fill="none" stroke="var(--bs-border-color)" strokeWidth="2" strokeDasharray="4 4" />
                <text x="14" y="126" fontSize="9" fill="var(--bs-secondary-color)">0°</text>
                <text x="94" y="24" fontSize="9" fill="var(--bs-secondary-color)">90°</text>
                <text x="174" y="126" fontSize="9" fill="var(--bs-secondary-color)">180°</text>
                <rect x="70" y="95" width="60" height="30" rx="4" fill="var(--bs-emphasis-color)" />
                <g style={{ transformOrigin: "100px 110px", transform: `rotate(${servoAng - 90}deg)` }}>
                  <rect x="97" y="40" width="6" height="70" rx="3" fill="var(--mot-accent)" />
                  <circle cx="100" cy="110" r="8" fill="var(--bs-body-bg)" stroke="var(--mot-accent)" strokeWidth="3" />
                </g>
              </svg>
              <svg width="220" height="60" viewBox="0 0 220 60" aria-hidden="true">
                <line x1="10" y1="45" x2="210" y2="45" stroke="var(--bs-border-color)" />
                <path d={servoPulsePath(servoUs)} fill="none" stroke="var(--mot-blue)" strokeWidth="2" />
                <text x="10" y="58" fontSize="9" fill="var(--bs-secondary-color)">0</text>
                <text x="200" y="58" fontSize="9" fill="var(--bs-secondary-color)">20 ms</text>
                <text x="30" y="10" fontSize="9" fill="var(--mot-blue)">
                  {(servoUs / 1000).toFixed(2).replace(".", ",")} ms
                </text>
              </svg>
              <div className="guia-mot-demo-ctrl">
                <label htmlFor="motServoUs">Largura do pulso (µs)</label>
                <input
                  type="range"
                  id="motServoUs"
                  min="1000"
                  max="2000"
                  step="10"
                  value={servoUs}
                  onChange={(e) => setServoUs(+e.target.value)}
                />
                <div className="guia-mot-demo-read">
                  <span>{servoUs} µs → {Math.round(servoAng)}°</span>
                  <small>
                    Arduino: <code>servo.write(angulo)</code> ou <code>servo.writeMicroseconds(us)</code>.
                    Servos baratos não chegam bem a 0°/180°; teste os limites reais para não forçar o mecanismo.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>Servo</th><th>Torque</th><th>Engrenagens</th><th>Serve para</th></tr>
              </thead>
              <tbody>
                <tr><td>SG90 (9 g)</td><td>≈ 1,8 kgf·cm</td><td>Plástico</td><td>Cabeça de sensor, garra leve, aprendizado</td></tr>
                <tr><td>MG90S</td><td>≈ 2,2 kgf·cm</td><td>Metal</td><td>Mesmo tamanho, dura mais</td></tr>
                <tr><td>MG996R</td><td>≈ 10 kgf·cm</td><td>Metal</td><td>Braço robótico pequeno, direção de carro</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Existe o <strong>servo de rotação contínua</strong>: o potenciômetro foi retirado, e o pulso passa a
            controlar velocidade e sentido em vez de ângulo. Vira um motor de roda com driver embutido, prático
            para robôs pequenos, mas sem precisão de posição.
          </p>
          <Refs id="3.1" />

          <h3 className="guia-mot-h3">3.2 Alimentando servos</h3>
          <div className="guia-mot-warn">
            Um SG90 parado consome quase nada, mas no instante em que começa a se mover pode puxar 500 mA ou
            mais. Ligar servos no pino 5V do Arduino funciona com <em>um</em> servo pequeno; com dois ou mais, a
            tensão cai, o Arduino reseta e o servo treme. Use uma fonte separada de 5 a 6 V (4 pilhas AA, um
            regulador ou um BEC) e junte os GNDs.
          </div>
          <p>Os três fios do servo: marrom/preto = GND, vermelho = alimentação, laranja/amarelo/branco = sinal.</p>
          <Refs id="3.2" />

          <h3 className="guia-mot-h3">3.3 Motor de passo: posição sem sensor</h3>
          <p>
            O motor de passo tem várias bobinas no estator e um rotor dentado magnetizado. Energizando as bobinas
            em sequência, o rotor avança um "passo" por vez, um ângulo fixo e conhecido. Um motor comum tem passo
            de <strong>1,8°</strong>, ou seja, <strong>200 passos por volta</strong>.
          </p>
          <p>
            Como cada passo é conhecido, você sabe onde o eixo está apenas contando passos. Não há sensor: é{" "}
            <strong>malha aberta</strong>. Por isso ele domina impressoras 3D, CNC e plotters. O ponto fraco: se
            a carga for pesada demais ou a aceleração for brusca, ele <strong>perde passos</strong> sem avisar, e
            sua contagem fica errada. Todo sistema com motor de passo tem um sensor de fim de curso para "zerar"
            a posição na partida.
          </p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th></th><th>Unipolar</th><th>Bipolar</th></tr>
              </thead>
              <tbody>
                <tr><td>Exemplo</td><td>28BYJ-48 (5 V, 5 fios)</td><td>NEMA 17 (12–24 V, 4 fios)</td></tr>
                <tr><td>Driver</td><td>ULN2003 (placa que vem junto)</td><td>A4988 ou DRV8825</td></tr>
                <tr><td>Torque</td><td>Baixo (≈ 0,3 kgf·cm)</td><td>Médio (≈ 4 kgf·cm)</td></tr>
                <tr><td>Uso</td><td>Aprendizado, torres, mecanismos leves</td><td>Impressora 3D, eixo linear, robô cartesiano</td></tr>
              </tbody>
            </table>
          </div>
          <p>Dois detalhes do A4988/DRV8825 que iniciantes pulam:</p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Detalhe</th><th>Descrição</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td><strong>Micropasso</strong></td><td>O driver pode dividir cada passo em 2, 4, 8 ou 16 partes. Ganha suavidade e resolução, perde um pouco de torque.</td></tr>
                <tr><td>2</td><td><strong>Ajuste de corrente (Vref)</strong></td><td>Um pequeno potenciômetro na placa limita a corrente que vai para o motor. Sem ajustar, o motor esquenta demais ou o driver desliga por proteção. Procure "ajuste de Vref A4988" antes do primeiro uso.</td></tr>
              </tbody>
            </table>
          </div>
          <CodeBlock code={`// Motor de passo bipolar no A4988: 1 pino de direção, 1 pino de passo
const int DIR = 2, STEP = 3;

void setup() { pinMode(DIR, OUTPUT); pinMode(STEP, OUTPUT); }

void girar(int passos, bool horario) {
  digitalWrite(DIR, horario);
  for (int i = 0; i < passos; i++) {
    digitalWrite(STEP, HIGH); delayMicroseconds(800);
    digitalWrite(STEP, LOW);  delayMicroseconds(800);
  }
}

void loop() { girar(200, true); delay(500); girar(200, false); delay(500); }`} />
          <Refs id="3.3" />

          <h3 className="guia-mot-h3">3.4 Qual escolher</h3>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>Se você precisa de...</th><th>Use</th><th>Porque</th></tr>
              </thead>
              <tbody>
                <tr><td>Um ângulo entre 0° e 180°, com força e rapidez</td><td>Servo</td><td>Malha fechada, driver embutido, um só fio de sinal</td></tr>
                <tr><td>Posição exata, muitas voltas, movimento repetitivo</td><td>Motor de passo</td><td>Contagem de passos dá posição sem sensor</td></tr>
                <tr><td>Girar continuamente (rodas)</td><td>DC com redução</td><td>Barato, eficiente, controle simples com PWM + ponte H</td></tr>
              </tbody>
            </table>
          </div>

          {renderQuiz(3)}
          <div className="guia-mot-nav-btns">
            <button className="guia-mot-btn" onClick={() => irPara(2)}>← Etapa 2</button>
            <button className="guia-mot-btn primary" onClick={() => irPara(4)}>Ir para a Etapa 4 →</button>
          </div>
        </div>
      )}

      {/* ===================== ETAPA 4 ===================== */}
      {etapaAtiva === 4 && (
        <div>
          <div className="guia-mot-day-head">
            <span className="guia-mot-day-num">4</span>
            <h2>Brushless, baterias e o primeiro protótipo</h2>
            <span className="guia-mot-day-time">≈ 2h30</span>
          </div>
          <div className="guia-mot-goal">
            <strong>Ao final da etapa você saberá:</strong> o que é um motor brushless e um ESC, escolher a
            bateria, estimar o torque que o seu robô precisa e reconhecer o papel de cada componente em um
            protótipo completo.
          </div>

          <h3 className="guia-mot-h3">4.1 Brushless (BLDC): o motor DC virado do avesso</h3>
          <p>
            No brushless, os ímãs ficam no rotor e as bobinas no estator, o contrário do DC escovado. Sem
            comutador e sem escovas: nada se desgasta, não faz faísca, e a eficiência é alta. Em troca, alguém
            precisa fazer a sequência de liga/desliga das bobinas: esse alguém é o <strong>ESC</strong>{" "}
            (Electronic Speed Controller).
          </p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Ponto-chave</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>O motor tem <strong>3 fios</strong>. Trocar dois deles entre si inverte o sentido.</td></tr>
                <tr><td>2</td><td>O ESC recebe do microcontrolador o <strong>mesmo sinal de um servo</strong> (pulso de 1 a 2 ms): 1 ms = parado, 2 ms = velocidade máxima. Na prática, você usa <code>Servo.h</code> para controlar um brushless.</td></tr>
                <tr><td>3</td><td>A ficha diz o <strong>kV</strong>: RPM por volt sem carga. Um motor de 1000 kV em 11,1 V gira a cerca de 11 100 RPM. kV alto = rápido e fraco; kV baixo = lento e forte.</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Para robôs com rodas, brushless só compensa quando você precisa de muita potência em pouco peso
            (robô de combate, corrida). Para o primeiro protótipo, fique no DC com redução.
          </p>
          <Refs id="4.1" />

          <h3 className="guia-mot-h3">4.2 Baterias</h3>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>Fonte</th><th>Tensão</th><th>Corrente que entrega</th><th>Quando usar</th></tr>
              </thead>
              <tbody>
                <tr><td>4 pilhas AA alcalinas</td><td>6 V (cai rápido)</td><td>Baixa</td><td>Servo solto, testes de bancada</td></tr>
                <tr><td>2 × 18650 (íon-lítio)</td><td>7,4 V nominal</td><td>Média-alta</td><td>Robô com 2 motores TT. Melhor custo-benefício.</td></tr>
                <tr><td>LiPo 2S / 3S</td><td>7,4 V / 11,1 V</td><td>Muito alta</td><td>Brushless, robôs rápidos. Exige cuidado.</td></tr>
                <tr><td>Fonte de bancada / USB</td><td>ajustável / 5 V</td><td>depende</td><td>Desenvolvimento. USB não serve para motores.</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Ler a bateria: <strong>3,7 V por célula</strong> de lítio; "2S" = 2 células em série = 7,4 V.{" "}
            <strong>mAh</strong> é a capacidade: 2000 mAh entregam 2 A por uma hora ou 1 A por duas horas. Em
            LiPo, a <strong>taxa C</strong> diz a corrente máxima: 2000 mAh 25C → 50 A.
          </p>
          <div className="guia-mot-warn">
            Nunca descarregue uma célula de lítio abaixo de 3,0 V nem carregue sem carregador balanceado.
            Baterias 18650 com placa de proteção (BMS) já cuidam disso; LiPo nua, não.
          </div>
          <p>
            O microcontrolador não pode receber 7,4 V no pino 5V. Use o pino <strong>VIN</strong> (que passa
            pelo regulador interno) ou um <strong>regulador/BEC</strong> externo de 5 V, e lembre:{" "}
            <strong>GND comum</strong>.
          </p>
          <Refs id="4.2" />

          <h3 className="guia-mot-h3">4.3 Quanto torque o meu robô precisa?</h3>
          <p>Para um robô com rodas em piso plano, uma estimativa suficiente para escolher motor:</p>
          <p className="guia-mot-formula">Torque por motor ≈ (m × g × r × μ × margem) ÷ nº de motores</p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Variável</th><th>Significado</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td><strong>m</strong></td><td>Massa total do robô (kg), incluindo bateria</td></tr>
                <tr><td>2</td><td><strong>g</strong></td><td>9,8 m/s²</td></tr>
                <tr><td>3</td><td><strong>r</strong></td><td>Raio da roda (m)</td></tr>
                <tr><td>4</td><td><strong>μ</strong></td><td>Resistência ao rolamento + folga para rampas leves. Use <strong>0,2</strong> para piso liso, <strong>0,4</strong> para carpete ou rampa</td></tr>
                <tr><td>5</td><td><strong>margem</strong></td><td>2 (motores de ficha chinesa entregam menos do que prometem)</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            <strong>Exemplo:</strong> robô de 1 kg, rodas de 65 mm (r = 0,0325 m), 2 motores, piso liso:<br />
            (1 × 9,8 × 0,0325 × 0,2 × 2) ÷ 2 = <strong>0,064 N·m ≈ 0,65 kgf·cm</strong> por motor.<br />
            O motor TT entrega ≈ 0,8 kgf·cm. Serve, mas sem folga para rampas. Para um robô de 2 kg ou com
            carpete, já pediria um motor com redução maior (ex.: 1:90) ou um motor de 12 V com encoder.
          </p>
          <p>
            Velocidade: a roda de 65 mm tem perímetro ≈ 0,204 m. A 200 RPM: 0,204 × 200 ÷ 60 ≈{" "}
            <strong>0,68 m/s</strong>. Bom para um robô de sala.
          </p>
          <Refs id="4.3" />

          <h3 className="guia-mot-h3">4.4 Checklist para qualquer motor em qualquer protótipo</h3>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Passo</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>O que o motor precisa fazer: girar continuamente, ir a um ângulo ou a uma posição exata? Isso escolhe o tipo (Etapa 1).</td></tr>
                <tr><td>2</td><td>Quanta força e velocidade? Estime torque (4.3) e escolha o modelo com margem.</td></tr>
                <tr><td>3</td><td>Qual tensão e corrente de stall? Isso escolhe a bateria (4.2) e o driver (Etapas 2 e 3).</td></tr>
                <tr><td>4</td><td>Fonte do motor separada da lógica, GNDs todos juntos.</td></tr>
                <tr><td>5</td><td>Teste o motor solto na bancada, com um código de 10 linhas, antes de montar no chassi.</td></tr>
                <tr><td>6</td><td>Só depois integre sensores e lógica.</td></tr>
              </tbody>
            </table>
          </div>

          <h3 className="guia-mot-h3">4.5 Exemplo: os componentes de um robô que anda e desvia de obstáculos</h3>
          <p>
            Para fechar o guia, um exercício de reconhecimento: pense em um robô de duas rodas que percorre uma
            sala desviando de obstáculos, mais os componentes para praticar os outros tipos de motor do guia.
            Estes são os componentes que um protótipo assim usaria — e o mapa de onde cada conceito das etapas se
            encaixa:
          </p>
          <div className="table-responsive">
            <table className="guia-mot-table">
              <thead>
                <tr><th>#</th><th>Componente</th><th>Por que ele entra</th></tr>
              </thead>
              <tbody>
                <tr><td>1</td><td><strong>Microcontrolador</strong> (ex.: Arduino Uno ou ESP32)</td><td>O cérebro: gera todos os sinais de controle estudados — PWM, pulsos de servo e de passo. Os limites de corrente dos seus pinos são o motivo de existirem os drivers (Etapa 1).</td></tr>
                <tr><td>2</td><td><strong>2 motores DC com redução</strong> (tipo TT) + rodas e roda boba</td><td>Giro contínuo para as rodas: a redução troca velocidade por torque (Etapa 2). Com dois motores independentes, o robô vira acionando cada lado em velocidade diferente.</td></tr>
                <tr><td>3</td><td><strong>Ponte H dupla</strong> (ex.: TB6612FNG)</td><td>Chaveia a potência dos dois motores: o PWM define a velocidade e as chaves em diagonal definem o sentido (Etapa 2), com perda de tensão desprezível.</td></tr>
                <tr><td>4</td><td><strong>Fonte separada para os motores</strong> (ex.: 2 × 18650 com proteção)</td><td>Entrega a corrente de stall que nenhum pino de microcontrolador fornece (Etapa 1), com a tensão e a capacidade dimensionadas na Etapa 4 — sempre com GND comum à lógica.</td></tr>
                <tr><td>5</td><td><strong>Servo pequeno</strong> (ex.: SG90)</td><td>Aponta a "cabeça" de sensor para ângulos exatos entre 0° e 180°: malha fechada com driver embutido, um só fio de sinal (Etapa 3).</td></tr>
                <tr><td>6</td><td><strong>Sensor ultrassônico</strong> (ex.: HC-SR04)</td><td>Mede a distância até os obstáculos. Montado sobre o servo, olha para os lados sem o robô precisar virar — é ele que dispara a decisão de desviar.</td></tr>
                <tr><td>7</td><td><strong>Motor de passo</strong> (ex.: 28BYJ-48 + placa ULN2003)</td><td>Não participa da locomoção: entra como bancada de prática de posição por contagem de passos, em malha aberta (Etapa 3).</td></tr>
                <tr><td>8</td><td><strong>Protoboard, jumpers e multímetro</strong></td><td>Ligações sem solda e medição de tensões e correntes reais — é assim que se verifica na prática a corrente de stall, quedas de tensão e o GND comum.</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Depois de terminar o quiz abaixo, o próximo passo natural é ler sobre <strong>encoders</strong>{" "}
            (medir quanto a roda girou de verdade) e <strong>controle PID</strong>, que transformam um robô que
            "mais ou menos anda reto" em um que anda reto.
          </p>

          {renderQuiz(4)}
          <div className="guia-mot-nav-btns">
            <button className="guia-mot-btn" onClick={() => irPara(3)}>← Etapa 3</button>
            <button className="guia-mot-btn primary" onClick={() => irPara(0)}>Voltar ao início ↩</button>
          </div>
        </div>
      )}

      {/* ===================== MODAL: EXERCÍCIOS EXTRAS ===================== */}
      {modalEtapa !== null && (() => {
        const qsExtra = exerciciosExtras[modalEtapa];
        const respondidas = qsExtra.filter((_, qi) => respostasExtra[`${modalEtapa}-${qi}`] !== undefined).length;
        const acertos = qsExtra.filter((_, qi) => respostasExtra[`${modalEtapa}-${qi}`]?.ok).length;
        return (
          <div className="guia-mot-modal-bg" onClick={() => setModalEtapa(null)}>
            <div className="guia-mot-modal" onClick={(e) => e.stopPropagation()}>
              <button className="guia-mot-modal-close" onClick={() => setModalEtapa(null)} aria-label="Fechar">
                ✕
              </button>
              <h2>EXERCÍCIOS EXTRAS DE FIXAÇÃO</h2>
              <div className="guia-mot-modal-subtitle">
                Etapa {modalEtapa} · {acertos} acertos em {respondidas} de {qsExtra.length} respondidas
              </div>
              {qsExtra.map((q, qi) =>
                renderQuestao(
                  q,
                  qi + 1,
                  respostasExtra[`${modalEtapa}-${qi}`],
                  ordemExtra[modalEtapa][qi],
                  (oi, ok) => responderExtra(modalEtapa, qi, oi, ok),
                  qi
                )
              )}
            </div>
          </div>
        );
      })()}
    </Container>
  );
}

export default GuiaMotores;
